/**
 * ReagendarDialog — mover una reserva a otra salida programada.
 *
 * Flujo:
 *   1. Mostrar info de la reserva actual (fecha actual)
 *   2. Selector visual de próximos 14 días con disponibilidad
 *   3. Click en día → carga salidas con cupo
 *   4. Seleccionar salida + motivo (opcional)
 *   5. Confirmar → backend mueve cupos y mantiene el código de reserva
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Stack, TextField, Button, Alert,
  CircularProgress, Chip, Divider,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SlideOver from '../common/SlideOver';
import useToast from '../common/useToast';
import {
  API_BASE, getAuthHeaders, getEmpresaId,
  formatFechaLarga, formatPrecio, formatHora,
} from './shared/format';

// Días a precargar en la fila horizontal. 14 cubre el caso normal (mover de
// día/semana). Si necesitan reagendar a >2 semanas, se cancela + se crea nueva.
const DIAS_VENTANA = 14;
const ESTADOS_TERMINALES = ['cancelada', 'suspendida', 'completada', 'no_show'];

const DIAS_SEMANA_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function localISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}


export default function ReagendarDialog({ reserva, onClose, onReagendada }) {
  const { toast, ToastContainer } = useToast();
  const [fechaNueva, setFechaNueva] = useState('');
  const [salidaSel, setSalidaSel] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salidasTodas, setSalidasTodas] = useState([]);   // todas las del rango 14 días
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const totalPax = (reserva.adultos || 0) + (reserva.ninos || 0) + (reserva.bebes || 0);

  // Ventana de 14 días — fijada al abrir el dialog
  const dias = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return Array.from({ length: DIAS_VENTANA }, (_, i) => {
      const d = new Date(hoy);
      d.setDate(d.getDate() + i);
      return { fecha: localISO(d), date: d };
    });
  }, []);

  // Una sola llamada al endpoint para todo el rango. No filtramos por `estado`
  // en backend (el filtro estricto bloqueaba salidas en estado 'activa' o
  // 'programada' indistintamente — ambos son vendibles). Filtramos localmente
  // excluyendo solo estados terminales.
  useEffect(() => {
    setCargando(true);
    const empresaId = getEmpresaId();
    const desde = dias[0].fecha;
    const hasta = dias[dias.length - 1].fecha;
    fetch(
      `${API_BASE}/turismo/salidas?empresa_id=${empresaId}&fecha_desde=${desde}&fecha_hasta=${hasta}`,
      { headers: getAuthHeaders() }
    )
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.salidas || []);
        setSalidasTodas(list);
      })
      .catch(() => toast.error('Error cargando salidas'))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mapa: fecha YYYY-MM-DD → { numDisponibles, suspendida, climaAlerta, mismoPaquete }
  const dispoPorDia = useMemo(() => {
    const map = {};
    for (const s of salidasTodas) {
      if (!s.fecha) continue;
      const fk = s.fecha.slice(0, 10);
      if (!map[fk]) map[fk] = { numDisponibles: 0, suspendida: false, climaAlerta: false, mismoPaquete: false, totalSalidas: 0 };
      map[fk].totalSalidas++;
      if (ESTADOS_TERMINALES.includes(s.estado)) {
        if (s.estado === 'suspendida' || s.estado === 'cancelada') map[fk].suspendida = true;
        continue;
      }
      // Capacidad efectiva (descuenta lanchas compartidas si aplica)
      const cap = s.capacidad_total || s.capacidad_override || 0;
      const ocupados = s.asientos_reservados || 0;
      const dispBase = (s.asientos_disponibles_efectivos != null)
        ? s.asientos_disponibles_efectivos
        : (cap - ocupados);
      // Si esta misma reserva está en esta salida y la cuenta se reusa, hay
      // un margen extra de totalPax. Para el badge del día con eso basta.
      const disp = dispBase + (s.id === reserva.salida_id ? totalPax : 0);
      if (disp >= totalPax) map[fk].numDisponibles++;
      if (s.estado_clima && s.estado_clima !== 'normal') map[fk].climaAlerta = true;
      if (s.paquete_nombre === reserva.paquete_nombre) map[fk].mismoPaquete = true;
    }
    return map;
  }, [salidasTodas, totalPax, reserva.salida_id, reserva.paquete_nombre]);

  // Salidas de la fecha seleccionada (filtradas + ordenadas)
  const salidasDelDia = useMemo(() => {
    if (!fechaNueva) return [];
    const todas = salidasTodas.filter(s => (s.fecha || '').slice(0, 10) === fechaNueva);
    const disponibles = todas.filter(s => {
      if (ESTADOS_TERMINALES.includes(s.estado)) return false;
      if (s.id === reserva.salida_id) return false;   // ya está en esta salida
      const cap = s.capacidad_total || s.capacidad_override || 0;
      const ocupados = s.asientos_reservados || 0;
      const disp = (s.asientos_disponibles_efectivos != null)
        ? s.asientos_disponibles_efectivos
        : (cap - ocupados);
      return disp >= totalPax;
    });
    disponibles.sort((a, b) => {
      const aMatch = (a.paquete_nombre === reserva.paquete_nombre) ? 0 : 1;
      const bMatch = (b.paquete_nombre === reserva.paquete_nombre) ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.hora_salida || '').localeCompare(b.hora_salida || '');
    });
    return disponibles;
  }, [salidasTodas, fechaNueva, reserva.salida_id, reserva.paquete_nombre, totalPax]);

  // Reset salida seleccionada cuando cambia la fecha
  useEffect(() => { setSalidaSel(''); }, [fechaNueva]);

  const confirmar = async () => {
    if (!salidaSel) { toast.error('Selecciona una salida'); return; }
    setEnviando(true);
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/reservaciones/${reserva.id}/reagendar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nueva_salida_id: parseInt(salidaSel),
          motivo: motivo.trim() || null,
          user_id: userId,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Error reagendando');
        setEnviando(false);
        return;
      }
      toast.success(`Reserva reagendada al ${new Date(data.fecha_nueva).toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })}`);
      setEnviando(false);
      onReagendada?.(data);
      setTimeout(onClose, 600);
    } catch (e) {
      toast.error('Error de red');
      setEnviando(false);
    }
  };

  return (
    <>
      <SlideOver open onClose={onClose}
        title="Reagendar reserva"
        subtitle={reserva.codigo_reserva}
        icon={<EventRepeatIcon />}
        footer={
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
            <Button variant="contained" disableElevation
              onClick={confirmar}
              disabled={!salidaSel || enviando}
              sx={{
                textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              }}>
              {enviando ? 'Reagendando…' : 'Confirmar reagendamiento'}
            </Button>
          </>
        }>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            La reserva se mueve a otra salida. Mantiene el código, precio y datos del cliente.
            Los cupos de la salida anterior se liberan automáticamente.
          </Alert>

          {/* Estado actual */}
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography sx={{ fontSize: 10, color: '#991b1b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Salida actual
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {formatFechaLarga(reserva.salida_fecha)} · {formatHora(reserva.salida_hora)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              {reserva.paquete_nombre || '—'} · {totalPax} pax · {formatPrecio(reserva.precio_total, reserva.moneda)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', color: '#0d9488' }}>
            <ArrowForwardIcon sx={{ fontSize: 24, transform: 'rotate(90deg)' }} />
          </Box>

          {/* Fila de 14 días con disponibilidad — selector visual */}
          <Box>
            <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, mb: 0.75, textTransform: 'uppercase' }}>
              Elegí nueva fecha
            </Typography>
            {cargando ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={20} sx={{ color: '#0d9488' }} />
              </Box>
            ) : (
              <Box sx={{
                display: 'flex', gap: 0.75, overflowX: 'auto', pb: 1,
                // Mejorar scroll en touch
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
              }}>
                {dias.map((d, idx) => (
                  <ChipDia key={d.fecha}
                    d={d}
                    dispo={dispoPorDia[d.fecha]}
                    seleccionado={fechaNueva === d.fecha}
                    esHoy={idx === 0}
                    onClick={() => setFechaNueva(d.fecha)}
                  />
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
              ¿Necesitás reagendar a más de 2 semanas? Cancelá esta reserva y creá una nueva.
            </Typography>
          </Box>

          {fechaNueva && salidasDelDia.length === 0 && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              No hay salidas con cupo disponible para esa fecha con {totalPax} pax.
            </Alert>
          )}

          {fechaNueva && salidasDelDia.length > 0 && (
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' }}>
                Salidas disponibles · {formatFechaLarga(fechaNueva + 'T12:00:00')}
              </Typography>
              {salidasDelDia.map(s => {
                const cap = s.capacidad_total || s.capacidad_override || 0;
                const libres = (s.asientos_disponibles_efectivos != null)
                  ? s.asientos_disponibles_efectivos
                  : (cap - (s.asientos_reservados || 0));
                const sel = parseInt(salidaSel) === s.id;
                const mismoPaquete = s.paquete_nombre === reserva.paquete_nombre;
                const lanchas = (s.embarcaciones_asignadas || []).map(e => e.embarcacion_nombre).filter(Boolean);
                return (
                  <Box key={s.id} onClick={() => setSalidaSel(String(s.id))}
                    sx={{
                      p: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: sel ? '2px solid #0d9488' : '1px solid #e2e8f0',
                      bgcolor: sel ? '#f0fdfa' : '#fff',
                      '&:hover': { borderColor: '#0d9488' },
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 180 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {s.paquete_nombre || s.paquete?.nombre}
                          </Typography>
                          {mismoPaquete && (
                            <Chip size="small" label="Mismo paquete"
                              sx={{ height: 16, fontSize: 9, bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          {formatHora(s.hora_salida)} → {formatHora(s.hora_regreso) || '—'}
                          {lanchas.length > 0 && ` · 🚤 ${lanchas.join(', ')}`}
                        </Typography>
                      </Box>
                      <Typography sx={{
                        fontSize: 11, fontWeight: 700,
                        color: libres > totalPax * 2 ? '#16a34a' : '#f59e0b',
                      }}>
                        {libres} cupo{libres !== 1 ? 's' : ''} disponible{libres !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {salidaSel && (
            <>
              <Divider />
              <TextField size="small" multiline minRows={2} label="Motivo del reagendamiento (opcional)"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: Cliente solicitó cambio. Alerta climática. Suspensión por DIMAR…" />
            </>
          )}
        </Stack>
      </SlideOver>
      <ToastContainer />
    </>
  );
}


function ChipDia({ d, dispo, seleccionado, esHoy, onClick }) {
  const disponibles = dispo?.numDisponibles || 0;
  const climaAlerta = dispo?.climaAlerta;
  const suspendida = dispo?.suspendida;
  const sinSalidas = !dispo || dispo.totalSalidas === 0;
  const habilitable = !sinSalidas && disponibles > 0;

  const dia = d.date;
  const diaSemana = DIAS_SEMANA_CORTOS[dia.getDay()];
  const numDia = dia.getDate();

  // Visual: 4 estados
  // - habilitable: chip activo (verde si seleccionado, blanco con border si no)
  // - sin cupos: chip ámbar (hay salidas pero llenas)
  // - sin salidas: chip gris claro deshabilitado
  // - suspendida: chip rojo (sale el ✕)
  let bg, fg, border, cursor = 'pointer';
  if (seleccionado) {
    bg = '#0d9488'; fg = '#fff'; border = '#0d9488';
  } else if (suspendida) {
    bg = '#fee2e2'; fg = '#991b1b'; border = '#fecaca';
  } else if (sinSalidas) {
    bg = '#f8fafc'; fg = '#94a3b8'; border = '#e2e8f0'; cursor = 'not-allowed';
  } else if (!habilitable) {
    bg = '#fef3c7'; fg = '#92400e'; border = '#fde68a'; cursor = 'not-allowed';
  } else {
    bg = '#fff'; fg = '#0f172a'; border = climaAlerta ? '#f59e0b' : '#e2e8f0';
  }

  return (
    <Box
      onClick={habilitable ? onClick : undefined}
      sx={{
        flex: '0 0 auto', minWidth: 76,
        p: 0.75, borderRadius: 1.5, textAlign: 'center',
        bgcolor: bg, color: fg,
        border: `2px solid ${border}`,
        cursor,
        scrollSnapAlign: 'start',
        opacity: sinSalidas ? 0.6 : 1,
        position: 'relative',
        transition: 'all 0.15s',
        '&:hover': habilitable && !seleccionado ? {
          borderColor: '#0d9488',
          boxShadow: '0 2px 6px rgba(13,148,136,0.15)',
        } : {},
      }}
    >
      {/* Marcar HOY con un dot */}
      {esHoy && (
        <Box sx={{
          position: 'absolute', top: 4, right: 4,
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: seleccionado ? '#fff' : '#0d9488',
        }} />
      )}
      <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, opacity: 0.85 }}>
        {diaSemana}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>
        {numDia}
      </Typography>
      {/* Línea inferior con info — varía según estado */}
      <Typography sx={{ fontSize: 9, fontWeight: 700, mt: 0.25 }}>
        {suspendida ? '✕ Susp.'
          : sinSalidas ? 'sin sal.'
          : !habilitable ? 'sin cupo'
          : `${disponibles} sal.`}
      </Typography>
      {/* Iconos de alerta */}
      {climaAlerta && !suspendida && !sinSalidas && (
        <Typography sx={{ fontSize: 10, lineHeight: 1 }}>⚠</Typography>
      )}
    </Box>
  );
}
